import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { ApiClient } from '@/types/services';
import { LoginRequest, LoginResponse } from '@/types/api';
import { Project, FloorPlan } from '@/types/index';
import logger from '@/utils/logger';
import { safeStorage } from '@/utils/safeStorage';

// ─── Local types ──────────────────────────────────────────────────────────────

/**
 * Normalized error shape returned by {@link MobileApiService.formatError}.
 * Every public method rejects with this object (never a raw AxiosError), so
 * callers have a stable contract.
 */
interface FormattedApiError {
  status: number;
  message: string;
  data: unknown;
  originalError: AxiosError;
}

/**
 * The subset of an error response body this service inspects. The backend
 * attaches `code` to signal *why* an auth request failed, `reason` to signal
 * which subscription gate tripped (402), and `message` for human-readable text.
 */
interface ApiErrorBody {
  code?: string;
  message?: string;
  reason?: string;
}

/** Reasons the 402 subscription gate can fire (server contract). */
type PaywallReason = 'trial_expired' | 'subscription_inactive' | 'no_subscription';

/**
 * 403 codes that mean "the token you sent is unusable" (vs. a plain
 * permission-denied 403, which carries none of these). Kept as a typed,
 * frozen set so the auth-failure rule reads as data, not scattered string
 * literals — the membership test below is behaviorally identical to the
 * original explicit `===` chain.
 */
const TOKEN_FAILURE_CODES: ReadonlySet<string> = new Set([
  'TOKEN_INVALID',
  'TOKEN_EXPIRED',
  'USER_NOT_FOUND',
  'ACCOUNT_DEACTIVATED',
]);

// ─── Service ──────────────────────────────────────────────────────────────────

// Universal API service - NO device detection, works for all devices.
//
// Automatic retries were removed: they caused failed calls (e.g. against
// the auth/register endpoint when the DB refused a user) to loop up to 5
// times and burn through the rate limiter. Each outbound call is now made
// exactly once; if a caller wants retry semantics it is responsible for
// implementing them with its own backoff policy.
class MobileApiService implements ApiClient {
  private api: AxiosInstance;

  constructor() {
    // For Render deployment: use relative URLs (empty string or '/')
    // Environment variable VITE_API_BASE_URL can override if needed
    const apiBaseURL = import.meta.env?.VITE_API_BASE_URL || '';
    logger.info('API service initialized', { baseURL: apiBaseURL || '(relative URLs)' });

    this.api = axios.create({
      baseURL: apiBaseURL,
      timeout: 15000, // Universal timeout - 15s works for all devices
      headers: {
        'X-App-Version': '1.0.0', // REMOVED device-specific headers
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor for authentication
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Try multiple token storage locations (using safeStorage for iOS Private Mode).
        // Order matters: 'auth_token' is the canonical key, 'token' is the legacy
        // fallback. Both are kept in sync on write, but we read both defensively.
        const token = safeStorage.getItem('auth_token') || safeStorage.getItem('token');

        logger.debug('🔍 API: Request interceptor token check', { hasToken: !!token, url: config.url });

        if (token && config.headers) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        // REMOVED: mobile-specific headers - universal approach

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor — token refresh + auth/billing gate handling.
    // No retry logic here: the original retry-with-backoff was removed to
    // avoid hammering the backend (and the rate limiter) on every failed
    // call. If a specific caller needs retry semantics, it implements its
    // own backoff on top of this service.
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Check for new token in response headers (sliding-session refresh).
        const newToken = response.headers['x-new-token'];
        if (typeof newToken === 'string' && newToken) {
          safeStorage.setItem('auth_token', newToken);
          safeStorage.setItem('token', newToken); // Also store with 'token' key
        }

        return response;
      },
      async (error: AxiosError<ApiErrorBody>) => {
        const status = error.response?.status;
        const body = error.response?.data;
        const code = body?.code;
        const url = error.config?.url || '';

        // Auth-failure detection. The server signals "the token you just sent
        // is unusable" via two shapes:
        //   • 401 — token missing/expired (incl. our explicit TOKEN_EXPIRED).
        //   • 403 — token rejected (TOKEN_INVALID), or the user it points to
        //           is gone/disabled (USER_NOT_FOUND / ACCOUNT_DEACTIVATED).
        // Permission-denied 403s (requireRole, requireOrgRole, org gates)
        // never carry one of these codes, so they pass through and the
        // caller handles them normally.
        //
        // Exception: 401 on /auth/login or /auth/register means "bad
        // credentials" — that's the form's own error to surface, not a
        // zombie-token. Don't wipe state there.
        const isLoginAttempt =
          url.includes('/auth/login') || url.includes('/auth/register');
        const isAuthFailure =
          !isLoginAttempt &&
          (status === 401 ||
            (status === 403 && code !== undefined && TOKEN_FAILURE_CODES.has(code)));

        if (status === 401 || status === 403) {
          logger.warn('API auth-related response', {
            url,
            status,
            code,
            message: body?.message,
            willClearAuth: isAuthFailure,
          });
        }

        if (isAuthFailure) {
          // Clear all auth data from storage
          safeStorage.removeItem('auth_token');
          safeStorage.removeItem('token');
          safeStorage.removeItem('user_data');

          // Remove auth header from future requests
          delete this.api.defaults.headers.common['Authorization'];

          // Reload to reset in-memory state (Zustand store) and show login screen
          window.location.reload();
          return Promise.reject(error);
        }

        // 402 Payment Required: subscription gate (server's
        // requireActiveSubscription middleware) rejected the request.
        // The backend shape is { success: false, reason: 'trial_expired'
        // | 'subscription_inactive' | 'no_subscription' }.
        //
        // We lazy-import('@/store') to avoid a circular import at module
        // init: store.ts imports this service, this service would otherwise
        // import the store back. Lazy access breaks the cycle.
        if (status === 402) {
          const reason: PaywallReason =
            (body?.reason as PaywallReason | undefined) || 'no_subscription';
          try {
            const store = (await import('@/store')).default;
            const api = store.getState();
            // Refresh the local subscription state from the server so the
            // paywall screen renders the latest values (trialEndsAt etc).
            await api.fetchSubscription();
            api.setPaywallReason(reason);
            api.setCurrentScreen('paywall');
          } catch (e) {
            logger.error('402 interceptor: failed to route to paywall', {
              error: (e as Error).message,
            });
          }
          return Promise.reject(this.formatError(error));
        }

        // No retry. Every non-2xx response that isn't one of the special
        // cases above is surfaced to the caller. If a component wants to
        // retry, it decides when and how.
        return Promise.reject(this.formatError(error));
      }
    );
  }

  private formatError(error: AxiosError): FormattedApiError {
    const { response, request, message } = error;

    if (response) {
      // Server responded with error status
      const responseBody = response.data as ApiErrorBody | null | undefined;
      return {
        status: response.status,
        message: responseBody?.message || `Server error: ${response.status}`,
        data: response.data,
        originalError: error,
      };
    } else if (request) {
      // Request was made but no response received
      return {
        status: 0,
        message: 'Network error: No response from server',
        data: null,
        originalError: error,
      };
    } else {
      // Something else happened
      return {
        status: 0,
        message: message || 'Unknown error occurred',
        data: null,
        originalError: error,
      };
    }
  }

  // API methods with mobile optimization
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await this.api.post<LoginResponse>('/auth/login', credentials);

      // Store token and user data
      if (response.data.success && response.data.data?.token) {
        safeStorage.setItem('auth_token', response.data.data.token);
        safeStorage.setItem('token', response.data.data.token); // Also store with 'token' key
        safeStorage.setItem('user_data', JSON.stringify(response.data.data.user));

        // Update API instance headers
        this.api.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.token}`;
      }

      return response.data;
    } catch (error) {
      // Clear any existing auth data on login failure
      safeStorage.removeItem('auth_token');
      safeStorage.removeItem('token');
      safeStorage.removeItem('user_data');
      delete this.api.defaults.headers.common['Authorization'];

      throw this.formatError(error as AxiosError);
    }
  }

  async createProject(projectData: Partial<Project>): Promise<Project> {
    try {
      logger.debug('🔍 API: Creating project with data:', projectData);

      // Check auth token before request
      const token = safeStorage.getItem('auth_token') || safeStorage.getItem('token');
      logger.debug('🔍 API: Auth token present', { tokenPresent: !!token });

      const response = await this.api.post<unknown>('/api/projects', projectData);

      logger.debug('🔍 API Version: Optimistic Fix v2 - Post Request Completed');
      logger.debug('🔍 API: Response status', { status: response.status });
      logger.debug('🔍 API: Response statusText', { statusText: response.statusText });
      logger.debug('🔍 API: Response.data', { data: response.data });

      // Check if response is successful
      if (response.status === 200 || response.status === 201) {
        // Treat the body as a loosely-shaped record so we can probe both the
        // nested ({ data: Project }) and direct (Project) payload formats.
        const data = response.data as { data?: Project; id?: unknown } | null | undefined;

        // Try standard nested format
        if (data && typeof data === 'object' && data.data) {
          logger.debug('✅ API: Using nested format response.data.data');
          return data.data;
        }

        // Try direct format (response.data IS the project)
        if (data && typeof data === 'object' && data.id) {
          logger.debug('✅ API: Using direct format response.data');
          return data as unknown as Project;
        }

        // If server returned success but no valid data, throw error instead of creating fake project
        logger.error('❌ API: Server returned success but response has no valid project data');
        throw new Error('Server returned invalid response format. Please try again.');
      }

      throw new Error(`Server returned unexpected status: ${response.status}`);
    } catch (error) {
      logger.error('❌ API: Full error object:', { error });
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        logger.error('❌ API: Error response data:', { data: axiosError.response.data });
        logger.error('❌ API: Error response status:', { status: axiosError.response.status });
      }
      throw this.formatError(error as AxiosError);
    }
  }

  async getProjects(): Promise<Project[]> {
    try {
      const response = await this.api.get<{ success: boolean; data: Project[] }>('/api/projects');
      return response.data.data; // Return the nested data array
    } catch (error) {
      throw this.formatError(error as AxiosError);
    }
  }

  async getFloorPlans(projectId: number): Promise<FloorPlan[]> {
    try {
      const response = await this.api.get<{ success: boolean; data: FloorPlan[] }>(
        `/api/floorplans/project/${projectId}`
      );
      return response.data.data; // Return the nested data array
    } catch (error) {
      throw this.formatError(error as AxiosError);
    }
  }

  async deleteProject(projectId: number): Promise<void> {
    try {
      await this.api.delete(`/api/projects/${projectId}`);
    } catch (error) {
      throw this.formatError(error as AxiosError);
    }
  }

  async updateProject(
    projectId: number,
    updateData: { name?: string; description?: string; is_public?: boolean }
  ): Promise<Project> {
    try {
      const response = await this.api.put<{ success: boolean; data: Project }>(
        `/api/projects/${projectId}`,
        updateData
      );
      return response.data.data;
    } catch (error) {
      throw this.formatError(error as AxiosError);
    }
  }

  // NOTE: uploadPhoto was removed — photos are device-only by design and are
  // never uploaded to the server. See utils/photoUtils for the full note.

  async getDeviceInfo(): Promise<{
    userAgent: string;
    screenSize: { width: number; height: number };
    networkType: string;
    batteryLevel: number | null;
  }> {
    // REMOVED: device detection - just return basic info
    const connection = (navigator as Navigator & { connection?: { effectiveType?: string } })
      .connection;
    return {
      userAgent: navigator.userAgent,
      screenSize: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      networkType: connection?.effectiveType || 'unknown',
      batteryLevel: null, // Will be updated if available
    };
  }

  // Health check for mobile connectivity
  async healthCheck(): Promise<{
    status: string;
    responseTime?: number;
    serverStatus?: number;
    error?: FormattedApiError;
  }> {
    try {
      const startTime = Date.now();
      const response = await this.api.get('/');
      const endTime = Date.now();

      return {
        status: 'healthy',
        responseTime: endTime - startTime,
        serverStatus: response.status,
      };
    } catch (error) {
      return {
        status: 'unhealthy',
        error: this.formatError(error as AxiosError),
      };
    }
  }

  // Generic HTTP methods for admin panel
  async get<T = unknown>(url: string): Promise<T> {
    try {
      const response = await this.api.get<T>(url);
      return response.data;
    } catch (error) {
      throw this.formatError(error as AxiosError);
    }
  }

  async post<T = unknown>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      const headers: Record<string, string> = { ...(config?.headers as Record<string, string>) };
      if (!(data instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      // For FormData, we let the browser set the Content-Type with the correct boundary
      const response = await this.api.post<T>(url, data, { ...config, headers });
      return response.data;
    } catch (error) {
      throw this.formatError(error as AxiosError);
    }
  }

  async put<T = unknown>(url: string, data: unknown): Promise<T> {
    try {
      const response = await this.api.put<T>(url, data);
      return response.data;
    } catch (error) {
      throw this.formatError(error as AxiosError);
    }
  }

  async patch<T = unknown>(url: string, data: unknown): Promise<T> {
    try {
      const response = await this.api.patch<T>(url, data);
      return response.data;
    } catch (error) {
      throw this.formatError(error as AxiosError);
    }
  }

  async delete<T = unknown>(url: string): Promise<T> {
    try {
      const response = await this.api.delete<T>(url);
      return response.data;
    } catch (error) {
      throw this.formatError(error as AxiosError);
    }
  }
}

// Create singleton instance
const mobileApiService = new MobileApiService();

// Export both default and named export for admin panel
export { mobileApiService as apiService };
export default mobileApiService;
